/*@jsxRuntime classic @jsx React.createElement @jsxFrag React.Fragment*/
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import React from "react";
import ExampleQuery from '../../components/ExampleQuery';
import JsonObjectPath from '../../components/JsonObjectPath';
import Layout from '../../components/Markdown';
const MDXLayout = Layout;
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    a: "a",
    span: "span",
    p: "p",
    h2: "h2",
    strong: "strong",
    code: "code",
    ul: "ul",
    li: "li"
  }, _provideComponents(), props.components);
  return React.createElement(React.Fragment, null, React.createElement(_components.h1, {
    id: "importing-eyeglass-prescriptions"
  }, "Importing Eyeglass Prescriptions", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#importing-eyeglass-prescriptions"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Importing eyeglass prescriptions requires both the customer and the member of staff to have an\naccount in Starfish. Staff accounts must be added through the Starfish application, but customer\naccounts can be created via the API. The steps below walk through fetching all the required data\nbefore importing the eyeglass prescription."), "\n", React.createElement(_components.p, null, "Unlike contact lens prescriptions, eyeglass prescriptions are not tied to a product — they carry\nthe measurements directly."), "\n", React.createElement(_components.h2, {
    id: "1-fetch-the-staff-member-from-starfish"
  }, "1. Fetch the staff member from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#1-fetch-the-staff-member-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Query the staffMember endpoint to get the staff member's Starfish ID. We use this ID when the\nmember of staff imports prescriptions via the API."), "\n", React.createElement(ExampleQuery, {
    name: "queryStaffMember",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "2-fetch-the-customer-from-starfish"
  }, "2. Fetch the customer from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#2-fetch-the-customer-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "If there is not already an account holder for this customer, follow the documentation\n", React.createElement(_components.a, {
    href: "/types/Mutation/createAccountHolder"
  }, "here"), " to create one."), "\n", React.createElement(_components.p, null, "Query the AccountHolder endpoint to get the customer's Starfish ID."), "\n", React.createElement(ExampleQuery, {
    name: "queryAccountHolder",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "3-resolve-the-patient"
  }, "3. Resolve the patient", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#3-resolve-the-patient"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Prescriptions are created against a ", React.createElement(_components.strong, null, "patient"), " — you pass ", React.createElement(_components.code, null, "patientId"), ", not the account\nholder's ID. Query the account holder's patients and pick the one the prescription is for — the\n", React.createElement(_components.code, null, "account_holder"), " patient for the customer themselves, or a dependant matched on\n", React.createElement(_components.code, null, "pmsPatientIdentifier"), "."), "\n", React.createElement(ExampleQuery, {
    name: "queryPatients",
    limitHeight: true
  }), "\n", React.createElement(_components.p, null, "If the prescription is for a dependant who does not exist yet, create them first. See\n", React.createElement(_components.a, {
    href: "/scenarios/patients-and-account-holders"
  }, "Patients and Account Holders"), " for the full\nexplanation of how patients relate to account holders."), "\n", React.createElement(_components.h2, {
    id: "4-fetch-the-location-from-starfish"
  }, "4. Fetch the location from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#4-fetch-the-location-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "If the prescription is an in-house prescription, you can pass this ID up when importing the\ncustomer's prescription. If you don't have this location's Starfish ID, you can fetch it from\nthe API:"), "\n", React.createElement(ExampleQuery, {
    name: "queryLocation",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "5-import-the-eyeglass-prescription"
  }, "5. Import the eyeglass prescription", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#5-import-the-eyeglass-prescription"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Now we have the patient's ID, the staff member's ID, and the location ID, we can import the\nprescription via the API following the documentation\n", React.createElement(_components.a, {
    href: "/types/Mutation/createEyeGlassPrescription"
  }, "here"), "."), "\n", React.createElement(_components.p, null, "The ", React.createElement(_components.code, null, "formType"), " argument controls which measurements apply:"), "\n", React.createElement(_components.ul, null, "\n", React.createElement(_components.li, null, React.createElement(_components.strong, null, React.createElement(_components.code, null, "simple")), " forms carry a single set of measurements (", React.createElement(_components.code, null, "simpleRightEye…"), " / ", React.createElement(_components.code, null, "simpleLeftEye…"), ")."), "\n"), "\n", React.createElement(ExampleQuery, {
    name: "createSimpleEyeGlassPrescription",
    limitHeight: true
  }), "\n", React.createElement(_components.ul, null, "\n", React.createElement(_components.li, null, React.createElement(_components.strong, null, React.createElement(_components.code, null, "advanced")), " forms carry separate ", React.createElement(_components.code, null, "distance"), ", ", React.createElement(_components.code, null, "intermediate"), " and ", React.createElement(_components.code, null, "near"), " measurements. An\n", React.createElement(_components.code, null, "add"), " value is supported on the simple, intermediate and near sets (distance has none)."), "\n"), "\n", React.createElement(ExampleQuery, {
    name: "createAdvancedEyeGlassPrescription",
    limitHeight: true
  }));
}
function MDXContent(props = {}) {
  return React.createElement(MDXLayout, props, React.createElement(_createMdxContent, props));
}
export default MDXContent;
